import type { InputFrame } from "../input";
import type { Skater } from "../skater";
import {
  POP_VARIANT_WINDOW,
  popVariantFor,
  SPECIAL_WINDOW,
  stickDir,
  stickDir8,
} from "../tricks";
import { bail } from "./bail";
import type { ManualState } from "./state";
import { HOLD_NUDGE, MANUAL_BUFFER, POP_NUDGE, SWITCH_FLIP_TIME } from "./tuning";

/** Up, down (manual) or down, up (nose manual): start it now, or on landing if it's close. */
export function readManualInput(sk: Skater, input: InputFrame, dt: number): void {
  const seq = sk.sticks.update(input.moveX, input.moveY, dt);
  sk.manualBuffer = Math.max(0, sk.manualBuffer - dt);
  // Any two-press sequence can also be the start of a special (up, down + grab).
  sk.specialSeqT = Math.max(0, sk.specialSeqT - dt);
  if (seq) {
    sk.specialSeq = seq;
    sk.specialSeqT = SPECIAL_WINDOW;
  } else if (sk.specialSeqT === 0) {
    sk.specialSeq = null;
  }
  sk.popSeqT = Math.max(0, sk.popSeqT - dt);
  if (seq && !sk.manual && popVariantFor(seq)) {
    sk.popSeq = seq;
    sk.popSeqT = POP_VARIANT_WINDOW;
  }
  // While manualing, up and down balance; they don't start another one. On foot they walk (#19).
  if (!seq || sk.manual || sk.mode === "bail" || sk.mode === "foot") return;
  const [a, b] = seq;
  if ((a === "u" && b === "d") || (a === "d" && b === "u")) {
    sk.manualBuffer = MANUAL_BUFFER;
    sk.manualNose = a === "d";
    sk.sticks.clear();
  }
}

export function startManual(sk: Skater): void {
  const trick = sk.manualNose ? sk.tricks.manual.nose : sk.tricks.manual.manual;
  sk.manualBuffer = 0;
  // The manual carries the combo on; how you landed into it isn't the finish.
  sk.landing = null;
  sk.manualFrom = { x: sk.x, z: sk.z };
  sk.manualGaps.clear();
  // Manualing out of a landing or a revert keeps the combo going instead of banking it.
  sk.revertWindow = 0;
  sk.linkWindow = 0;
  sk.manual = {
    trick,
    index: sk.addTrick(trick.name, trick.points),
    balance: sk.manualCarry ?? (sk.rng() - 0.5) * 0.3,
    balanceVel: 0,
    time: 0,
    pop: null,
    hold: null,
  };
}

/**
 * THPS 2 flatground tricks off a manual: flip + direction pops a quick
 * flip or shove-it, grab + direction holds a Casper, Pogo and so on. Each
 * one kicks the needle toward the wheels that are down.
 */
export function stepManualTricks(sk: Skater, m: ManualState, input: InputFrame, dt: number): void {
  const toward = m.trick.nose ? 1 : -1;
  if (m.pop) {
    m.pop.t += dt;
    if (m.pop.t >= m.pop.duration) m.pop = null;
  }
  if (m.hold) {
    if (input.held.grab) {
      m.hold.t += dt;
      sk.combo.addPoints(m.hold.index, m.hold.trick.perSecond * dt);
    } else {
      m.hold = null;
    }
  }
  if (m.pop || m.hold) return;
  const dir = stickDir(input.moveX, input.moveY);
  if (input.pressed.flip) {
    // The stick at the press, not `flipAim`'s look back: in a manual it was just balancing.
    const trick = sk.tricks.manualPop[stickDir8(input.moveX, input.moveY)];
    const duration = trick.duration * (sk.switchStance ? sk.switchPenalty(SWITCH_FLIP_TIME) : 1);
    m.pop = {
      trick,
      index: sk.addTrick(trick.name, trick.points),
      t: 0,
      duration,
    };
    m.balanceVel += POP_NUDGE * toward;
  } else if (input.pressed.grab && sk.tricks.manualHold) {
    const trick = ((m.trick.nose && sk.tricks.noseHold) || sk.tricks.manualHold)[dir];
    m.hold = { trick, index: sk.addTrick(trick.name, trick.points), t: 0 };
    m.balanceVel += HOLD_NUDGE * toward;
  }
}

/** Manual: the combo stays open and keeps scoring while you balance with up / down. True if it bailed. */
export function stepManual(sk: Skater, m: ManualState, input: InputFrame, dt: number): boolean {
  m.time += dt;
  sk.balanceTime += dt;
  sk.combo.addPoints(m.index, m.trick.perSecond * dt);
  stepManualTricks(sk, m, input, dt);
  if (sk.stepBalance(m, input.moveY, dt, sk.wobble(m.hold ? "hold" : "manual"))) {
    bail(sk, sk.out);
    return true;
  }
  return false;
}
